import { theme } from '@side/pantry';
import { isAfter, startOfMonth } from 'date-fns';
import { useMemo } from 'react';
import { usePayment } from '$api-clients/hooks';
import { useCompensationReportFilters } from '../CompensationReportFilters/CompensationReportFilters.helpers';
import {
  buildReportingSummaryQuery,
  getAllTimePeriods,
  getLast13Periods,
  getPeriodsForDateRange,
  isReportingPeriodAllTime,
  isReportingPeriodCustom,
  isReportingPeriodL365,
  useViewReportAs,
} from '../helpers';
import { formatPeriodMonthLabel } from '../PerformanceReport/hooks/useGetPerformanceData.helpers';
import { useAllTimeStartDate } from '../ReportsConfiguration/useReportsConfiguration';
import {
  type ReportsDataGroup,
  useReportsRole,
  useRoleCanSee,
} from '../ReportsConfiguration/useReportsRole';
import {
  MONTHS,
  type ReportingSummaryResult,
  type ReportsRole,
} from '../types';

export type MonthlyEarningsFields = Partial<
  Pick<
    ReportingSummaryResult,
    | 'periodMonth'
    | 'agentGrossShare'
    | 'teamGrossShare'
    | 'brandGrossShare'
    | 'sideNetShare'
    | 'weightedAdjustedGrossCommission'
  >
> & { monthLabel: string };

const monthlyEarningsDataGroups: Record<
  keyof MonthlyEarningsFields,
  ReportsDataGroup
> = {
  agentGrossShare: 'agent',
  brandGrossShare: 'brand',
  monthLabel: 'agent',
  periodMonth: 'agent',
  sideNetShare: 'side',
  teamGrossShare: 'team',
  weightedAdjustedGrossCommission: 'agent',
};

export function useMonthlyEarnings() {
  const viewReportAs = useViewReportAs();
  const { roleCanSee } = useRoleCanSee();
  const { filters } = useCompensationReportFilters();
  const allTimeStartDate = useAllTimeStartDate();
  const query = viewReportAs
    ? buildReportingSummaryQuery(filters, viewReportAs, allTimeStartDate)
    : undefined;

  const isL365 = isReportingPeriodL365(filters?.reportingPeriod);
  const isCustom = isReportingPeriodCustom(filters?.reportingPeriod);
  const isAllTime = isReportingPeriodAllTime(filters?.reportingPeriod);

  const periods = useMemo(() => {
    if (isL365) return getLast13Periods();
    if (isCustom && filters?.customDateStart && filters?.customDateEnd) {
      return getPeriodsForDateRange(
        filters.customDateStart,
        filters.customDateEnd,
      );
    }
    if (isAllTime) return getAllTimePeriods(allTimeStartDate);
    return null;
  }, [
    isL365,
    isCustom,
    isAllTime,
    filters?.customDateStart,
    filters?.customDateEnd,
    allTimeStartDate,
  ]);

  const dynamicLimit = periods ? periods.length : 12;

  const {
    data: monthlyEarningsData,
    isLoading,
    error,
  } = usePayment(
    '/reporting/summary',
    query
      ? {
          params: {
            query: {
              ...query,
              period: 'MONTHLY',
              transactionStatus: ['CLOSED'],
              offset: 0,
              limit: dynamicLimit,
            },
          },
        }
      : null,
  );

  const monthlyEarnings = useMemo<MonthlyEarningsFields[]>(() => {
    const results = monthlyEarningsData?.results ?? [];
    const buildRow = (
      row: ReportingSummaryResult | undefined,
      periodMonth: number,
      monthLabel: string,
    ): MonthlyEarningsFields => ({
      periodMonth,
      monthLabel,
      agentGrossShare: row?.agentGrossShare ?? 0,
      ...(roleCanSee('team')
        ? { teamGrossShare: row?.teamGrossShare ?? 0 }
        : {}),
      ...(roleCanSee('brand')
        ? { brandGrossShare: row?.brandGrossShare ?? 0 }
        : {}),
      ...(roleCanSee('side') ? { sideNetShare: row?.sideNetShare ?? 0 } : {}),
      weightedAdjustedGrossCommission:
        row?.weightedAdjustedGrossCommission ?? 0,
    });

    if (periods) {
      return periods.map(({ year, month }, index) => {
        const row = results.find(
          (r) =>
            (r as { periodYear?: number }).periodYear === year &&
            r.periodMonth === month,
        );
        return buildRow(row, index, formatPeriodMonthLabel(year, month));
      });
    }
    return MONTHS.map((month, index) =>
      buildRow(
        results.find((r) => r.periodMonth === index + 1),
        index,
        month,
      ),
    );
  }, [monthlyEarningsData?.results, periods, roleCanSee]);

  return {
    monthlyEarnings,
    isLoading,
    error,
    noResults: monthlyEarningsData?.results.length === 0,
  };
}

const SERIES_CONFIG: {
  dataKey: keyof MonthlyEarningsFields;
  label: string;
  color: string | undefined;
}[] = [
  {
    dataKey: 'agentGrossShare',
    label: 'Agent Gross',
    color: '#5C6AFF',
  },
  {
    dataKey: 'teamGrossShare',
    label: 'Team Gross',
    color: theme.palette.secondary[600],
  },
  {
    dataKey: 'brandGrossShare',
    label: 'Brand Gross',
    color: theme.palette.secondary[400],
  },
  {
    dataKey: 'sideNetShare',
    label: 'Side Gross',
    color: theme.palette.moonstone[400],
  },
];

export function getMonthlyEarningsFieldLabelByRole(
  dataKey: keyof MonthlyEarningsFields,
  role: ReportsRole,
) {
  if (role === null && dataKey === 'agentGrossShare') {
    return 'My Gross Commission';
  }

  if (role === null && dataKey === 'weightedAdjustedGrossCommission') {
    return 'My Weighted GCI';
  }

  return undefined;
}

export function useSeriesConfig() {
  const { role = null, isLoading } = useReportsRole();
  const { roleCanSee } = useRoleCanSee();
  return useMemo(
    () =>
      isLoading
        ? []
        : SERIES_CONFIG.filter((c) =>
            roleCanSee(monthlyEarningsDataGroups[c.dataKey]),
          ).map((c) => ({
            ...c,
            label:
              getMonthlyEarningsFieldLabelByRole(c.dataKey, role) ?? c.label,
          })),
    [role, isLoading, roleCanSee],
  );
}

export const ADJUSTED_GCI_CONFIG = {
  dataKey: 'weightedAdjustedGrossCommission',
  label: 'Post-Referral GCI',
  color: theme.palette.grey[300],
} as const;

export const IMAGE_NAME = 'monthly-earnings-timeline.png';

/**
 * Returns true if the given month (0–11) in the given year is in the future
 * relative to today. Used to show gray placeholder bars only for future months.
 */
export function isFutureMonth({
  selectedYear,
  monthIndex,
}: {
  selectedYear: number;
  monthIndex: number;
}): boolean {
  const targetMonth = new Date(selectedYear, monthIndex, 1);
  const currentMonth = startOfMonth(new Date());
  return isAfter(targetMonth, currentMonth);
}

const PREFERRED_TICK_COUNT = 5;

/**
 * Generates "nice" y-axis tick values from 0 up to a rounded ceiling of maxVal.
 * Uses a step size that yields readable numbers (e.g. 20k, 50k, 100k).
 */
export function getNiceYAxisTicks(
  maxVal: number,
  preferredTickCount = PREFERRED_TICK_COUNT,
): number[] {
  if (maxVal <= 0) return [0, 20_000, 40_000, 60_000, 80_000, 100_000];

  const roundTick = (v: number) => Number(v.toFixed(6));
  const targetTickCount = preferredTickCount + 1; // includes 0

  const roughStep = maxVal / preferredTickCount;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));

  // Candidate step sizes that tend to stay "nice" for both small and big values.
  const multipliers = [1, 2, 2.5, 5, 10];
  type Best = {
    step: number;
    ticks: number[];
    scorePrimary: number;
    scoreSecondary: number;
    scoreTertiary: number;
  };

  let best: Best | undefined;

  for (const multiplier of multipliers) {
    const step = multiplier * magnitude;
    if (!Number.isFinite(step) || step <= 0) {
      // Skip invalid candidate steps.
    } else {
      const tickMax = Math.ceil(maxVal / step) * step;
      const epsilon = Math.max(1e-9, tickMax * 1e-12);

      const ticks: number[] = [0];
      for (let t = step; t <= tickMax + epsilon; t += step) {
        ticks.push(roundTick(t));
      }

      // De-dupe in case of floating errors.
      const normalizedTicks = ticks.filter(
        (v, i) => i === 0 || Math.abs(v - ticks[i - 1]) > 1e-6,
      );

      const tickCount = normalizedTicks.length;
      const overshoot = tickMax - maxVal;
      const scoreSecondary = overshoot / Math.max(maxVal, 1);
      const scoreTertiary = Math.abs(step - roughStep) / Math.max(roughStep, 1);

      const scorePrimary = Math.abs(tickCount - targetTickCount);

      const candidate: Best = {
        step,
        ticks: normalizedTicks,
        scorePrimary,
        scoreSecondary,
        scoreTertiary,
      };

      if (!best) {
        best = candidate;
      } else {
        const isBetter =
          candidate.scorePrimary < best.scorePrimary ||
          (candidate.scorePrimary === best.scorePrimary &&
            (candidate.scoreSecondary < best.scoreSecondary ||
              (candidate.scoreSecondary === best.scoreSecondary &&
                candidate.scoreTertiary < best.scoreTertiary)));

        if (isBetter) best = candidate;
      }
    }
  }

  // Fallback: should be rare, but keeps the function total.
  if (!best) return [0, 20_000, 40_000, 60_000, 80_000, 100_000];

  return best.ticks;
}
